import { createSelector } from '@reduxjs/toolkit';
import Layer from '@/components/layers/Layer';
import TableRef from '@/util/TableRef';
import { UUID } from '@/types';
import { ApplicationState } from '@/features/publicDatasets/types';
import ViewRef from '@/util/ViewRef';
import Column from '@/util/Column';
import SuggestedView from '@/util/SuggestedView';
import DataTable from '@/util/DataTable';
import DataTableHeading from '@/util/DataTableHeading';
import { FROM_SAVED_QUERY } from '@/util/enums/EnumLayers';
import LayerJoin from '@/components/layers/LayerJoin';

export const getDatasourceDbType = (state: {
  datasource: { dbType: string };
}) => state.datasource.dbType;

export const getDatasourceTablesWithCreatedAt = (state: {
  datasource: { tablesWithCreatedAt: TableRef[] };
}) => state.datasource.tablesWithCreatedAt;

export const getDatasourceTables = (state: {
  datasource: { tables: TableRef[] };
}) => state.datasource.tables;

export const getDashboardsState = (state: ApplicationState) => state.dashboards;

export const getDashboardRefreshId = (state: ApplicationState) =>
  state.dashboards.refreshId;

export const getDashboardsEnabled = (state: ApplicationState) =>
  state.datasource.dashboardsEnabled;

export const getIsDatasourceOnFreePlan = (state: ApplicationState) =>
  state.datasource.maxSteps < 999;

export const getIsDatasourceLite = (state: ApplicationState) =>
  state.datasource.isLite;

export const getIsDatasourceProPlus = (state: ApplicationState) =>
  state.datasource.isProPlus;

export const getIsLoadingView = (state: ApplicationState) =>
  state.layers.loadingView;

export const getLayers = (state: ApplicationState) => state.layers;

export const getIsSelfHosted = (state: ApplicationState) =>
  state.datasource.isSelfHosted;

export const getDatasourceErrorMessage = (state: ApplicationState) =>
  state.datasource.errorMsg;
export const getIsAdmin = (state: ApplicationState) => state.datasource.isAdmin;
export const getIsGuest = (state: ApplicationState) => state.datasource.isGuest;
export const getIsOwner = (state: ApplicationState) => state.datasource.isOwner;
export const getIsCreditPricing = (state: ApplicationState) =>
  state.datasource.isCreditsPricing;
export const getDatasourceName = (state: ApplicationState) =>
  state.datasource.name;
export const getIsConfigureEnabled = (state: ApplicationState) =>
  state.datasource.configureEnabled;

export const getUserEmail = (state: ApplicationState) =>
  state.datasource.userEmail;

export const getActiveDashboard = (state: ApplicationState) =>
  state.dashboards.active;
export const getActiveDashboardId = (state: {
  dashboards: { active: { id: F0<UUID> } };
}) => state.dashboards.active.id();

export const getActiveDashboardName = (state: {
  dashboards: { active: { name: F0<string> } };
}) => state.dashboards.active.name();

export const getPublicDatasetMetadata = (state: ApplicationState) => ({
  metadata: state.rows.metadata,
  rowRequestId: state.rows.rowRequestId,
});

export const getRowRequestId = (state: ApplicationState) =>
  state.rows.rowRequestId;

export const getSelectedView = (state: {
  layers: { selectedView: ViewRef | SuggestedView };
}) => state.layers.selectedView;

export const getLayersSelectedViewId = (state: {
  layers: { selectedView: ViewRef };
}) => state.layers.selectedView.id();

export const getLayersSelectedViewSeriesId = (state: {
  layers: { selectedView: ViewRef };
}) => state.layers.selectedView?.series();

export const getDatasourceMasterTables = (state: {
  datasource: { masterTables: TableRef[] };
}) => state.datasource.masterTables;

export const getQueryBuilderSteps = (state: { layers: { all: Layer[] } }) =>
  state.layers.all;

export const getQueryBuilderFirstStep = (state: { layers: { all: Layer[] } }) =>
  getQueryBuilderSteps(state)[0];

export const getDatasourceReadyFlag = (state: ApplicationState) => ({
  datasourceReady: state.datasource.isReady,
  headings: state.rows.headings,
});

export const getIsDatasourceReady = (state: ApplicationState) =>
  state.datasource.isReady;

export const getSavedQueriesCount = (state: {
  views: { savedQueriesCount: number };
}) => state.views.savedQueriesCount;

export const getRowsHeadings = (state: ApplicationState) => state.rows.headings;

export const getDatasourceId = (state: {
  datasource: { datasourceId: UUID };
}) => state.datasource.datasourceId;

export const getNavigationData = (state: ApplicationState) => ({
  rowRequestId: state.rows.rowRequestId,
  datasourceReady: state.datasource.isReady,
  isSaved: state.views.showShareBar,
  currentTab: state.datasource.currentTab,
  dashboard: state.dashboards.active,
  view: state.views.lastLoadedView,
  dataPresentation: state.rows.dataPresentation,
});

export const getDashboardViews = (state: ApplicationState) =>
  state.dashboards.views;

export const getDashboardViewsAwaitingResults = (state: ApplicationState) =>
  state.dashboards.viewsAwaitingResults;

export const getDashboardFilters = (state: ApplicationState) =>
  state.dashboards.filters;

export const getDashboardAppliedFilters = (state: ApplicationState) =>
  state.dashboards.appliedFilters;

export const getDashboardChartWidgets = (state: ApplicationState) =>
  state.dashboards.chartWidgets;

export const getDataPresentation = (state: ApplicationState) =>
  state.rows.dataPresentation;

export const getTimerCount = (state: { rows: { timerCount: string } }) =>
  state.rows.timerCount;

export const getViews = (state: ApplicationState) => state.views;

export const getLastLoadedView = (state: ApplicationState) =>
  state.views.lastLoadedView;

export const getShowShareBar = (state: ApplicationState) =>
  state.views.showShareBar;

export const getAliases = (state: ApplicationState) => state.layers.usedAliases;

export const getSelectedLayerId = (state: {
  layers: { selectedLayerId: number };
}) => state.layers.selectedLayerId;

export const getCollapsedLayerId = (state: {
  layers: { collapsedLayerId: Maybe<number> };
}) => state.layers.collapsedLayerId;

export const getLayerInEditMode = (state: {
  layers: { layerInEditMode: Nullable<Layer> };
}) => state.layers.layerInEditMode;

export const getLayerInEditModeIndex = (state: ApplicationState) => {
  const { layerInEditMode, all } = state.layers;
  if (!layerInEditMode) return undefined;

  return all.findIndex((s) => s._id === layerInEditMode._id);
};
export const getLastLayerAdded = (state: {
  layers: { lastLayerAdded: Nullable<Layer> };
}) => state.layers.lastLayerAdded;

export const getLayerErrorMessage = (state: {
  layers: { layerErrorMsg: Nullable<string> };
}) => state.layers.layerErrorMsg;

export const getUserActions = (state: {
  datasource: { userActions: Record<string, boolean> };
}) => state.datasource.userActions;

export const getAllColumns = (state: { rows: { allColumns: Column[] } }) =>
  state.rows.allColumns;

export const getColumnsByLayerId = (state: {
  rows: { columnsByLayerId: Record<number, Column[]> };
}) => state.rows.columnsByLayerId;

export const getIsSavedQueryBeingSaved = (state: {
  modals: { saveLayersModal: { saving: boolean } };
}) => state.modals.saveLayersModal.saving;

export const getChartingOptions = (state: {
  layers: { chartingOptions: object };
}) => state.layers.chartingOptions;

export const getRowsErrorMessage = (state: { rows: { errorMsg: string } }) =>
  state.rows.errorMsg;

export const getShowChartingOptions = (state: {
  layers: { showChartingOptions: boolean };
}) => state.layers.showChartingOptions;

export const getAlertsDisabled = (state: {
  datasource: { alertsDisabled: boolean };
}) => state.datasource.alertsDisabled;

export const getExportsDisabled = (state: {
  datasource: { exportsDisabled: boolean };
}) => state.datasource.exportsDisabled;

export const getFeedsDisabled = (state: {
  datasource: { feedsDisabled: boolean };
}) => state.datasource.feedsDisabled;

export const getDataTable = (state: { rows: { dataTable: DataTable } }) =>
  state.rows.dataTable;

export const getTotalRowCount = (state: { rows: { totalRowCount: number } }) =>
  state.rows.totalRowCount;

export const getPage = (state: { layers: { page: number } }) =>
  state.layers.page;

export const getLoadingRowsRequest = (state: {
  layers: { loadingRowsRequest: boolean };
}) => state.layers.loadingRowsRequest;

export const getLoadingQuerySteps = (state: { layers: { loading: boolean } }) =>
  state.layers.loading;

export const getRowsLoading = (state: { rows: { loading: boolean } }) =>
  state.rows.loading;

export const getRowsAllResults = (state: {
  rows: { allResults: Nullable<boolean> };
}) => state.rows.allResults;

export const getRowsTimerStart = (state: { rows: { timerStart: number } }) =>
  state.rows.timerStart;

export const getCurrentPks = (state: {
  rows: { currentPks: { _alias: string; _name: string }[] };
}) => state.rows.currentPks;

export const getIsFirstStepFromSavedQuery = (state: {
  layers: { all: Layer[] };
}) => state.layers?.all?.[0]?._type === FROM_SAVED_QUERY;

export const getIsAnyJoinFromSavedQuery = (state: {
  layers: { all: Layer[] };
}) =>
  state.layers?.all?.filter(
    (s) =>
      s._type === 'JOIN' &&
      s.enabled() &&
      (s as LayerJoin).fk().foreignColumn().tov() instanceof ViewRef,
  ).length > 0;

export const getIsAnyStepFromSavedQuery = createSelector(
  getIsFirstStepFromSavedQuery,
  getIsAnyJoinFromSavedQuery,
  (isFirstStepFromSavedQuery, isAnyJoinFromSavedQuery) =>
    isFirstStepFromSavedQuery || isAnyJoinFromSavedQuery,
);

export const getTableHeadings = (state: {
  rows: { dataTable: Maybe<DataTable>; headings: Maybe<Column[]> };
}) =>
  state.rows.dataTable // if datatable is available, use its headings
    ? state.rows.dataTable.headings()
    : state.rows.headings // otherwise fallback to just columns, if they're available
    ? state.rows.headings.map((c) => DataTableHeading.fromColumn(c))
    : null;
